import React, { createContext, useContext, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GetAccountChart } from '../graphql/accountChart';
import { buildCreditCardIdToAccountKeyMap } from '../utils/accountChartCreditCardHelpers';
import { buildPasswordIdToAccountKeyMap } from '../utils/accountChartBankHelpers';

const AccountsContext = createContext({
  accounts: [],
  accountChartId: null,
  creditCardIdToAccountKeyMap: new Map(),
  passwordIdToAccountKeyMap: new Map(),
  loading: false,
  error: null,
  refetchAccounts: () => {},
});

export const AccountsProvider = ({ children }) => {
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const hasMounted = useRef(false);

  const { data, fetching, error, reexecuteQuery } = GetAccountChart({
    companyId
  });

  // Refetch accounts when company changes (skip initial mount — urql already fires on mount)
  useEffect(() => {
    if (companyId && hasMounted.current) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
    hasMounted.current = true;
  }, [companyId]);

  const value = useMemo(() => {
    const chart = data?.accountChart;
    const accounts = chart?.accounts || [];
    const rawChartId = chart?._id ?? chart?.id;
    return {
      accounts,
      accountChartId: rawChartId != null ? String(rawChartId) : null,
      creditCardIdToAccountKeyMap: buildCreditCardIdToAccountKeyMap(accounts),
      passwordIdToAccountKeyMap: buildPasswordIdToAccountKeyMap(accounts),
      loading: fetching,
      error,
      refetchAccounts: reexecuteQuery,
    };
  }, [data, fetching, error, reexecuteQuery]);

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  );
};

export const useAccounts = () => {
  const context = useContext(AccountsContext);
  if (!context) {
    throw new Error('useAccounts must be used within AccountsProvider');
  }
  return context;
};

